import {
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  differenceInCalendarMonths,
  differenceInCalendarQuarters,
  format,
  parse,
  startOfDay,
  endOfDay,
} from 'date-fns';

import { type PeriodInterval } from '../../models/periodInterval';

/**
 * Format as an ISO string, as the local time zone.
 *
 * Implementation note:
 *   Alternatively DateFns.formatISO() could be used too.
 */
const toLocalISOString = (date: Date) =>
  format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");

/**
 * Parse an ISO or a date only (yyyy-MM-dd) string
 * without converting to local time zone.
 * Always return the start of the day in local time.
 */
const parseDateAsLocal = (dateString: string) =>
  parse(dateString.slice(0, 10), 'yyyy-MM-dd', new Date('0000-01-01'));

/**
 * Convert a serialized period to a Period object.
 */
export const buildPeriod = (period: {
  start: string;
  end: string;
}): PeriodInterval => ({
  start: startOfDay(parseDateAsLocal(period.start)),
  end: endOfDay(parseDateAsLocal(period.end)),
});

/**
 * Convert a Period object to a serialized period.
 * Uses local date/time without UTC conversion.
 */
export const buildApiPeriod = (
  period: PeriodInterval,
): { start: string; end: string } => ({
  start: toLocalISOString(period.start),
  end: toLocalISOString(period.end),
});

export type Granularity = 'monthly' | 'quarterly' | 'yearly' | 'overall';

export const alignPeriodToGranularity = (
  period: PeriodInterval,
  granularity: Granularity,
): PeriodInterval => {
  switch (granularity) {
    case 'overall':
    case 'yearly':
    case 'monthly': {
      return {
        start: startOfMonth(period.start),
        end: endOfMonth(period.end),
      };
    }
    case 'quarterly': {
      return {
        start: startOfQuarter(period.start),
        end: endOfQuarter(period.end),
      };
    }
    default:
      throw new Error(`Unsupported granularity: ${granularity}`);
  }
};

/**
 * Return the number of allocation values for a given period and granularity.
 *
 * Granularity alignment is unnecessary as already accounted for internally.
 */
export const getAllocationLength = (
  period: PeriodInterval,
  granularity: Granularity,
) => {
  switch (granularity) {
    case 'yearly':
    case 'monthly':
      return differenceInCalendarMonths(period.end, period.start) + 1;
    case 'quarterly':
      return differenceInCalendarQuarters(period.end, period.start) + 1;
    case 'overall':
      return 1;
    default:
      throw new Error(`Unsupported granularity: ${granularity}`);
  }
};
